import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { addAlert } from "service/CSRAdminAlerts";
import { getAdminAlerts } from "redux/slices/getAdminAlertsSlice";
import { useTranslation } from "react-i18next";
import { convertToTitleCase } from "js/formatting";

import Message from "components/CSRAdmin/SendAnAlert/Form/Message";
import PortalType from "components/CSRAdmin/SendAnAlert/Form/PortalType";
import MessageType from "components/CSRAdmin/SendAnAlert/Form/MessageType";
import EndDate from "components/CSRAdmin/SendAnAlert/Form/EndDate";
import StartDate from "components/CSRAdmin/SendAnAlert/Form/StartDate";
import Tooltip from "components/General/Tooltip";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";

function EditForm({
  alert,
  setConfirmationModal,
  setConfirmationMessage,
  setOpenAlertEdit,
}) {
  const dispatch = useDispatch();
  const [isMessageFill, setIsMessageFill] = useState(true);
  const [selectedOptionPortal, setSelectedOptionPortal] = useState({
    value: alert?.portal ?? "",
  });
  const [selectedOption, setSelectedOption] = useState({
    value: alert?.group ?? "",
  });
  const [selectedOptionTitle, setSelectedOptionTitle] = useState({
    value: alert?.title?.replace(" ", "")?.toLowerCase() ?? "",
  });
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    control,
    setError,
    formState: { errors },
    reset: resetEditAlertForm,
  } = useForm({
    defaultValues: {
      type: alert?.type,
      portal: alert?.portal,
      userView: convertToTitleCase(alert?.userView),
      startDate: moment(moment(alert?.startDate).format("YYYY-MM-DD")).format(
        "YYYY-MM-DD"
      ),
      endDate: moment(moment(alert?.endDate).format("YYYY-MM-DD")).format(
        "YYYY-MM-DD"
      ),
      message: alert?.message,
      title: alert?.title?.replace(" ", "")?.toLowerCase(),
    },
  });
  const { t } = useTranslation();

  const submitAlert = async () => {
    const payload = {
      ...alert,
      group: getValues("group"),
      type: getValues("type"),
      portal: getValues("portal"),
      userView: getValues("userView"),
      startDate: getValues("startDate"),
      endDate: getValues("endDate"),
      message: getValues("message"),
      title:
        alert?.title?.replace(" ", "")?.toLowerCase() === getValues("title")
          ? alert?.title
          : getValues("title"),
    };

    await addAlert(payload).then((res) => {
      if (res.ok) {
        setConfirmationMessage(t("AgentCSR-Admin-EditForm-message-1"));
        setConfirmationModal(true);
        setOpenAlertEdit(false);
      } else {
        setConfirmationMessage(t("AgentCSR-Admin-EditForm-message-2"));
        setConfirmationModal(true);
        setOpenAlertEdit(false);
      }
    });
  };
  const isDisabled =
    !getValues("portal") ||
    !getValues("startDate") ||
    !getValues("endDate") ||
    !getValues("title") ||
    !isMessageFill;

  return (
    <>
      <form
        className="flex flex-col px-10"
        onSubmit={handleSubmit(submitAlert)}
      >
        <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-8 py-4">
          <PortalType
            errors={errors}
            setValue={setValue}
            control={control}
            setError={setError}
            selectedOption={selectedOptionPortal}
            setSelectedOption={setSelectedOptionPortal}
          />
          <div>
            <MessageType
              errors={errors}
              setValue={setValue}
              control={control}
              setError={setError}
              getValues={getValues}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          </div>
          <div>
            <div className="flex items-center mb-1">
              <label id={`portalPage-label`}>
                {t("AgentCSR-Admin-ActiveAlert-title-3")}
              </label>
              <Tooltip
                xMargin="mx-0"
                content={t("CsrAdminPortalPage-tooltip-verbiage")}
              />
            </div>
            <input
              id={`userView`}
              disabled={true}
              className="w-full"
              type="text"
              {...register("userView")}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-8">
          <StartDate
            register={register}
            setValue={setValue}
            errors={errors}
            isDisabled={true}
          />

          <EndDate
            register={register}
            setValue={setValue}
            errors={errors}
            startDate={getValues("startDate")}
          />
        </div>
        <div className="py-4">
          <Controller
            name="alertTitle"
            control={control}
            render={() => (
              <RELEASING_AutoCompleteComboBox
                labelName="Title of Alert"
                inputPlaceholderText="Select an alert"
                options={[
                  {
                    value: "",
                    displayAs: "",
                    selectedPolicyDisplayAs: "Select an alert",
                    isDisplayOnly: true,
                  },
                  {
                    value: "importantnotice",
                    displayAs: t("CsrAdmin-important-notice"),
                    selectedPolicyDisplayAs: t("CsrAdmin-important-notice"),
                    isDisplayOnly: true,
                  },
                  {
                    value: "systemmaintenance",
                    displayAs: t("CsrAdmin-system-maintenance"),
                    selectedPolicyDisplayAs: t("CsrAdmin-system-maintenance"),
                    isDisplayOnly: true,
                  },
                ]}
                selectedOption={selectedOptionTitle}
                setSelectedOption={(option) => {
                  if (option?.value) {
                    setValue("title", convertToTitleCase(option?.displayAs));
                    setSelectedOptionTitle(option?.value);
                  }
                }}
              />
            )}
            rules={{
              required: "This is a required field.",
            }}
          />
        </div>
        <div>
          <Message
            register={register}
            errors={errors}
            setError={setError}
            setIsMessageFill={setIsMessageFill}
            MESSAGEMAXIMUMWORDCOUNT={
              selectedOption?.value === "banner" ? 500 : 300
            }
          />
        </div>
        <div className="flex justify-end">
          <button
            className="ui-btn-primary ui-btn-min-w mt-4"
            disabled={isDisabled}
            onClick={() => submitAlert()}
          >
            {t("AgentCSR-Admin-Form-button")}
          </button>
        </div>
      </form>
    </>
  );
}

export default EditForm;
